import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Instagram, Share2 } from "lucide-react";
import gsap from "gsap";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 48 48" width="18" height="18" fill="none" aria-hidden="true" {...props}>
    <path d="M31 8.5c2.4 2.7 5 4.2 8.3 4.5v5.1c-3.4-.2-6.1-1.3-8.3-3V30c0 6.6-5 11.5-11.7 11.5S7.5 36.6 7.5 30c0-6.3 4.8-11.2 11-11.5v5.4c-3 .3-5.5 2.8-5.5 6.1 0 3.5 2.7 6.1 6.1 6.1 3.6 0 6.1-2.6 6.1-6.1V6.5H31v2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Professional social dock: compact glassy pill that expands on hover/focus (desktop), always open on mobile
// Optional: show only after a specific section comes into view (desktop)
const SocialDock = ({ showAfterSelector, hideInSelector }) => {
  const root = useRef(null);
  const pill = useRef(null);
  const items = useRef([]);
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  // Always open on small screens for touch ergonomics
  useLayoutEffect(() => {
    const update = () => setOpen(window.innerWidth < 640);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    if (!pill.current) return;
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
    const targetW = open ? 140 : 48;
    tl.to(pill.current, { width: targetW, duration: 0.35 }, 0);
    gsap.to(items.current, { opacity: open ? 1 : 0, y: open ? 0 : 4, stagger: 0.05, duration: 0.25, ease: "power2.out" });
  }, [open]);

  // Control visibility using IntersectionObserver: becomes visible once the target section enters the viewport (desktop)
  useEffect(() => {
    const isDesktop = window.innerWidth >= 768;
    if (!isDesktop) {
      setVisible(false);
      return;
    }
    const target = showAfterSelector ? document.querySelector(showAfterSelector) : null;
    const hideTargets = [];
    const selectors = hideInSelector
      ? Array.isArray(hideInSelector) ? hideInSelector : String(hideInSelector).split(',')
      : ['#hero', 'footer'];
    selectors.forEach((sel) => {
      const el = document.querySelector(sel.trim());
      if (el) hideTargets.push(el);
    });

    let hasReached = false;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (target && entry.target === target) {
            if (entry.isIntersecting) {
              hasReached = true;
              setVisible(true);
            } else if (!hasReached) {
              setVisible(false);
            }
          }
          if (hideTargets.includes(entry.target)) {
            if (entry.isIntersecting) setVisible(false);
            else if (hasReached) setVisible(true);
          }
        });
      },
      { root: null, threshold: 0.05 }
    );
    if (target) observer.observe(target);
    hideTargets.forEach((el) => observer.observe(el));

    // Fallback initial check
    if (target) {
      const rect = target.getBoundingClientRect();
      if (rect.top < window.innerHeight) {
        hasReached = true;
        setVisible(true);
      }
    }
    // If any hide target is currently visible, hide dock
    hideTargets.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        setVisible(false);
      }
    });

    const onResize = () => {
      // keep logic simple on resize: if section is within viewport at all, ensure visible
      if (target) {
        const r = target.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          hasReached = true;
          setVisible(true);
        }
      }
      hideTargets.forEach((el) => {
        const rr = el.getBoundingClientRect();
        if (rr.top < window.innerHeight && rr.bottom > 0) {
          setVisible(false);
        }
      });
    };
    window.addEventListener('resize', onResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [showAfterSelector]);

  const LINKS = [
    { label: "TikTok", href: "https://www.tiktok.com/@tom.oppenhaeuser?_t=ZN-8yyhumYA4uI&_r=1", Icon: TikTokIcon },
    { label: "Instagram", href: "https://www.instagram.com/tomoppenhauser?igsh=MXFtZ3pkYm5rMzE1bQ%3D%3D&utm_source=qr", Icon: Instagram }
  ];

  return (
    <div
      ref={root}
      className="relative select-none"
      onMouseEnter={() => window.innerWidth >= 640 && setOpen(true)}
      onMouseLeave={() => window.innerWidth >= 640 && setOpen(false)}
      style={{
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transform: visible ? 'translateY(0px)' : 'translateY(6px)',
        transition: 'opacity 180ms ease, transform 180ms ease'
      }}
    >
      {/* Glass pill */}
      <div
        ref={pill}
        className="flex items-center gap-1.5 h-12 w-12 rounded-full border border-white/60 bg-white/40 backdrop-blur-md shadow-sm overflow-hidden"
        style={{
          WebkitBackdropFilter: "blur(8px)",
          boxShadow: "0 6px 24px rgba(0,0,0,0.08), inset 0 0 0 1px rgba(0,0,0,0.04)"
        }}
        aria-label="Soziale Medien"
      >
        {/* Trigger icon (always visible) */}
        <button
          className="ml-2 flex-none w-8 h-8 grid place-items-center rounded-full bg-white/90 text-neutral-700 hover:text-neutral-900 shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-300"
          aria-label="Social öffnen"
          onClick={() => setOpen((s) => !s)}
        >
          <Share2 size={16} />
        </button>

        {/* Items */}
        {LINKS.map(({ label, href, Icon }, idx) => (
          <a
            key={label}
            ref={(el) => (items.current[idx] = el)}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            className="opacity-0 translate-y-[4px] w-8 h-8 grid place-items-center rounded-full text-neutral-700 hover:text-neutral-900 bg-white/90 shadow hover:shadow-md transition"
          >
            <Icon />
          </a>
        ))}
      </div>
    </div>
  );
};

export default SocialDock;